import { ShareData } from '@components/modal/ShareModal';
import { ShowStatus } from '@typings/show';

export type SharePlatform = 'x' | 'facebook' | 'whatsapp' | 'email';

export type ShareOptions = {
  title: string;
  text?: string;
  url: string;
};

export type SocialShareOptions = {
  platform: SharePlatform;
  title: string;
  url: string;
  description?: string;
};

const BASE_URL = process.env.REACT_APP_WEB_SITE || window.location.origin;

export const generateShowShareData = ({
  id,
  status,
  title,
  image,
}: {
  id: string;
  status: ShowStatus;
  title: string;
  image?: string | null;
}): ShareData => {
  const getShowUrl = () => {
    switch (status) {
      case ShowStatus.Live:
        return `${BASE_URL}/show/${id}`;
      case ShowStatus.Finished:
        return `${BASE_URL}/recorded-show/${id}`;
      case ShowStatus.Scheduled:
        return `${BASE_URL}/scheduled-show/${id}`;
      default:
        break;
    }
  };

  const getShowDescription = () => {
    const statusText =
      status === ShowStatus.Live
        ? 'Live now'
        : status === ShowStatus.Scheduled
          ? 'Scheduled'
          : 'Recorded';

    return `${statusText} show on Omili`;
  };

  return {
    id,
    title,
    description: getShowDescription(),
    image,
    url: getShowUrl() || '',
    type: 'show',
  };
};

export const generateProfileShareData = (profileId: string): ShareData => {
  return {
    id: profileId,
    title: `Check out this profile on Omili!`,
    url: `${BASE_URL}/profile/${profileId}`,
    type: 'profile',
  };
};

export const isWebShareSupported = (): boolean => {
  return typeof navigator !== 'undefined' && 'share' in navigator;
};

export const shareContent = async (options: ShareOptions): Promise<void> => {
  if (isWebShareSupported()) {
    try {
      await navigator.share(options);
    } catch (error) {
      console.error('Error sharing:', error);
      window.open(options.url, '_blank');
    }
  } else {
    window.open(options.url, '_blank');
  }
};

export const generateSocialShareUrl = (options: SocialShareOptions): string => {
  const { platform, title, url, description } = options;
  const encodedTitle = encodeURIComponent(title);
  const encodedUrl = encodeURIComponent(url);
  const encodedDescription = encodeURIComponent(description || '');

  switch (platform) {
    case 'x':
      return `https://x.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`;
    case 'facebook':
      return `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
    case 'whatsapp':
      return `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`;
    case 'email':
      return `mailto:?subject=${encodedTitle}&body=${encodedDescription}%0A%0A${encodedUrl}`;
    default:
      return url;
  }
};

export const openSocialShare = (options: SocialShareOptions): void => {
  const shareUrl = generateSocialShareUrl(options);
  window.open(shareUrl, '_blank');
};
